import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

/**
 * Campos de formulario (RNF15 / RNF16).
 *
 * Todo campo nasce com rotulo visivel ligado por `htmlFor`, dica opcional e
 * erro anunciado por `aria-describedby`. Placeholder nunca substitui rotulo:
 * some ao digitar e nao e lido por leitor de tela como nome do campo.
 *
 * Os controles tem 44px de altura (`h-11`) para o toque no celular (RNF16); a
 * variante `sm` (36px) e para filtros dentro de tabelas, nunca para o
 * formulario principal.
 */

const CONTROL =
  'w-full rounded-lg border border-line bg-surface px-3 text-sm text-ink shadow-soft transition-colors placeholder:text-ink-subtle focus:border-brand disabled:cursor-not-allowed disabled:opacity-60';

export function Field({
  label,
  hint,
  error,
  required = false,
  children,
  htmlFor,
  className = '',
}: {
  readonly label: ReactNode;
  readonly hint?: ReactNode;
  readonly error?: string | null;
  readonly required?: boolean;
  /** O controle. Recebe `id` e `aria-describedby` via render prop. */
  readonly children: (ids: { readonly id: string; readonly describedBy: string | undefined; readonly invalid: boolean }) => ReactNode;
  readonly htmlFor?: string;
  readonly className?: string;
}): JSX.Element {
  const gerado = useId();
  const id = htmlFor ?? gerado;
  const hintId = `${id}-dica`;
  const errorId = `${id}-erro`;
  const temErro = error !== undefined && error !== null && error !== '';
  const describedBy = [hint === undefined ? null : hintId, temErro ? errorId : null].filter((v): v is string => v !== null).join(' ') || undefined;

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required ? <span aria-hidden="true" className="ml-0.5 text-brand">*</span> : null}
      </label>
      {children({ id, describedBy, invalid: temErro })}
      {hint === undefined ? null : (
        <p id={hintId} className="text-xs text-ink-subtle">
          {hint}
        </p>
      )}
      {temErro ? (
        <p id={errorId} role="alert" className="text-xs text-status-critical">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function Input({ size = 'md', className = '', ...rest }: { readonly size?: 'sm' | 'md'; readonly className?: string } & Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'className'>): JSX.Element {
  return <input className={`${CONTROL} ${size === 'sm' ? 'h-9' : 'h-11'} ${className}`} {...rest} />;
}

export function Select({ size = 'md', className = '', children, ...rest }: { readonly size?: 'sm' | 'md'; readonly className?: string; readonly children: ReactNode } & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'className' | 'children'>): JSX.Element {
  return (
    <select className={`${CONTROL} ${size === 'sm' ? 'h-9' : 'h-11'} appearance-none bg-[length:1rem] bg-[right_0.75rem_center] bg-no-repeat pr-9 ${className}`} style={{ backgroundImage: CHEVRON_DATA_URI }} {...rest}>
      {children}
    </select>
  );
}

const CHEVRON_DATA_URI =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23898781' stroke-width='1.7' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")";

export function Textarea({ className = '', rows = 4, ...rest }: { readonly className?: string } & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'>): JSX.Element {
  return <textarea rows={rows} className={`${CONTROL} min-h-[6rem] py-2.5 leading-relaxed ${className}`} {...rest} />;
}

/**
 * Interruptor ligado/desligado. Usa `role="switch"` num botao: o estado e lido
 * como "ligado"/"desligado" e nao como "marcado", que e a semantica do checkbox.
 */
export function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled = false,
}: {
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
  readonly label: ReactNode;
  readonly description?: ReactNode;
  readonly disabled?: boolean;
}): JSX.Element {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <span id={id} className="block text-sm font-medium text-ink">
          {label}
        </span>
        {description === undefined ? null : <span className="mt-0.5 block text-xs text-ink-subtle">{description}</span>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-labelledby={id}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 shrink-0 rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
          checked ? 'border-status-good bg-status-good' : 'border-edge bg-ink/10'
        }`}
      >
        <span aria-hidden="true" className={`absolute top-0.5 h-[18px] w-[18px] rounded-full bg-white shadow-soft transition-transform ${checked ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  description,
  disabled = false,
}: {
  readonly checked: boolean;
  readonly onChange: (next: boolean) => void;
  readonly label: ReactNode;
  readonly description?: ReactNode;
  readonly disabled?: boolean;
}): JSX.Element {
  const id = useId();
  return (
    <label htmlFor={id} className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${checked ? 'border-brand/50 bg-brand/5' : 'border-line bg-surface hover:border-edge'} ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
      <input id={id} type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 rounded border-edge accent-[rgb(var(--color-brand))]" />
      <span className="min-w-0">
        <span className="block text-sm text-ink">{label}</span>
        {description === undefined ? null : <span className="mt-0.5 block text-xs text-ink-subtle">{description}</span>}
      </span>
    </label>
  );
}

/** Grupo de campos com titulo, para formularios longos (cadastro de empresa). */
export function Fieldset({ legend, description, children }: { readonly legend: string; readonly description?: ReactNode; readonly children: ReactNode }): JSX.Element {
  return (
    <fieldset className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-5 shadow-soft">
      <legend className="px-1 text-sm font-semibold text-ink">{legend}</legend>
      {description === undefined ? null : <p className="-mt-2 text-xs text-ink-subtle">{description}</p>}
      {children}
    </fieldset>
  );
}
